import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";

/**
 * The chat text size choices (P1.9-T224, the author's request of 2026-10-31, "accessibility in settings needs options
 * to increase chat size"): Normal, Large, Larger and Largest, which scale the chat panel's message text and entry text
 * to 100, 125, 150 and 175 percent. The stored text is the choice.
 */
export type ChatTextChoice = "normal" | "large" | "larger" | "largest";

/**
 * The choices in the order Settings offers them.
 */
export const CHAT_TEXT_CHOICES: readonly ChatTextChoice[] = ["normal", "large", "larger", "largest"];

/**
 * Each choice's multiple of the chat text, in quarters, so the caption's 8 logical px line height lands on whole
 * logical pixels at every choice (8, 10, 12 and 14). The text is Noto Sans, not the pixel face, so its size need not
 * be a whole number of device pixels: at an odd UI scale 125 and 175 percent of the 6 logical px caption are
 * fractional device sizes, which the face renders without the crispness the pixel art needs.
 */
export const CHAT_TEXT_SCALE: Readonly<Record<ChatTextChoice, number>> = Object.freeze({ normal: 1, large: 1.25, larger: 1.5, largest: 1.75 });

/**
 * The attribute the page root carries so the chat's style rules read the choice.
 */
export const CHAT_TEXT_ATTRIBUTE = "data-chat-text";

/**
 * The storage key the choice is kept under, per device like the chat panel's size.
 */
export const CHAT_TEXT_KEY = "odyssey.chat.text";

/**
 * Reads a kept value as a choice.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns The choice it names, Normal for anything else.
 */
export function parseChatText(raw: string | null): ChatTextChoice {
  return CHAT_TEXT_CHOICES.find((choice) => choice === raw) ?? "normal";
}

/**
 * Reads the kept choice.
 *
 * @param store Where it is kept, or null when there is none.
 * @returns The choice, Normal when nothing usable is kept or the store cannot be read.
 */
export function readChatText(store: SettingsStore | null = localStore()): ChatTextChoice {
  return readSetting(CHAT_TEXT_KEY, parseChatText, store);
}

/**
 * Keeps a choice.
 *
 * @param choice The choice.
 * @param store Where to keep it, or null when there is none.
 * @returns True when the store took it.
 */
export function writeChatText(choice: ChatTextChoice, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(CHAT_TEXT_KEY, choice, store);
}

/**
 * The page's chat text size: made from what the store holds as the page loads, it puts the choice on the root element
 * the chat's rules read, applies each change at once with no reload, and keeps it per device.
 *
 * @example
 * ```ts
 * const chatText = new ChatTextSetting(readChatText(), document.documentElement);
 * chatText.setChoice("larger");
 * ```
 */
export class ChatTextSetting {
  private current: ChatTextChoice;

  /**
   * @param choice The choice as the page loads, as `readChatText` gives it.
   * @param root The element that carries the choice as `data-chat-text`.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    choice: ChatTextChoice,
    private readonly root: Pick<HTMLElement, "setAttribute">,
    private readonly store: SettingsStore | null = localStore(),
  ) {
    this.current = choice;
    root.setAttribute(CHAT_TEXT_ATTRIBUTE, choice);
  }

  /**
   * The choice in force.
   *
   * @returns The choice.
   */
  get choice(): ChatTextChoice {
    return this.current;
  }

  /**
   * Takes Settings' choice, which the chat shows at once, and keeps it.
   *
   * @param choice The choice.
   */
  setChoice(choice: ChatTextChoice): void {
    this.current = choice;
    this.root.setAttribute(CHAT_TEXT_ATTRIBUTE, choice);
    writeChatText(choice, this.store);
  }
}
